import { useEffect } from 'react';
import {
	useForm,
	UseFormProps,
	UseFormReturn,
	FieldValues
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodType, ZodTypeDef } from 'zod';

type SchemaFor<TOutput> = ZodType<TOutput, ZodTypeDef, any>;

interface UseEntityFormOptions<T extends FieldValues>
	extends Omit<UseFormProps<T>, 'mode' | 'defaultValues'> {
	/**
	 * Form mode - determines reset behavior
	 * - 'create': Fresh form with default values
	 * - 'edit': Form populated with existing data
	 * - 'clone': Form populated with cloned data (submits as new entity)
	 */
	mode: 'create' | 'edit' | 'clone';

	/**
	 * Initial data for edit/clone mode
	 */
	initialData?: T;

	/**
	 * Default values for create mode
	 */
	defaultValues: T;

	/**
	 * Optional Zod schema used for validation. When provided the form
	 * will automatically use the schema through the zodResolver.
	 */
	schema?: SchemaFor<T>;
}

/**
 * Standard hook for entity forms that handles:
 * - Automatic form reset based on mode and data changes
 * - Consistent form management across all entities
 * - Guaranteed clean state for create mode forms
 *
 * Note: Form routes (/create and /edit) are never cached by the routing system,
 * ensuring forms always load with fresh state.
 *
 * @example
 * ```tsx
 * // Create mode
 * const form = useEntityForm({
 *   mode: 'create',
 *   defaultValues: { name: '', description: '' }
 * });
 *
 * // Edit mode
 * const form = useEntityForm({
 *   mode: 'edit',
 *   defaultValues: { name: '', description: '' },
 *   initialData: editData
 * });
 *
 * // Clone mode (pre-populate with cloned data)
 * const clonedData = cloneEntityWithNewIds(sourceData);
 * const form = useEntityForm({
 *   mode: 'clone',
 *   defaultValues: { name: '', description: '' },
 *   initialData: clonedData
 * });
 *
 * const { control, handleSubmit, formState: { errors } } = form;
 * ```
 */
export const useEntityForm = <T extends FieldValues>({
	mode,
	initialData,
	defaultValues,
	schema,
	...formOptions
}: UseEntityFormOptions<T>): UseFormReturn<T> => {
	// For edit and clone modes, use initialData if provided
	// For create mode, use default values
	const formDefaultValues =
		(mode === 'edit' || mode === 'clone') && initialData 
			? initialData 
			: defaultValues;

	const form = useForm<T>({
		mode: 'onChange', // Enable onChange validation for better UX
		resolver: schema ? zodResolver(schema) : undefined,
		defaultValues: formDefaultValues as any,
		...formOptions
	});

	// For create mode, ensure the form is always reset to default values
	// whenever the component renders - this guarantees clean state
	useEffect(() => {
		if (mode === 'create') {
			form.reset(defaultValues);
		}
	}, [mode]); // Only depend on mode to avoid resetting while user is typing

	// Handle edit and clone mode data changes
	useEffect(() => {
		if ((mode === 'edit' || mode === 'clone') && initialData) {
			form.reset(initialData);
		}
	}, [mode, initialData]);

	return form as UseFormReturn<T>;
};

/**
 * Type helper for form submit handlers
 */
export type EntityFormSubmitHandler<T> = (data: T) => void | Promise<void>;

/**
 * Standard interface for entity form components
 */
export interface EntityFormProps<TFormData, TSubmitData = TFormData> {
	mode: 'create' | 'edit' | 'clone';
	initialData?: TFormData;
	onSubmit: EntityFormSubmitHandler<TSubmitData>;
	isSubmitting: boolean;
}
